import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, useToast } from '@vd/ui';
import { Sparkles, ShieldCheck } from 'lucide-react';
import { useCurrentCompany } from '../../../features/auth/AuthProvider';
import { api } from '../../../lib/api';
import styles from '../Settings.module.css';

/**
 * AI features tab. One toggle today (auto-title generation), but lives
 * on its own page so future AI features (smart triage, comment
 * summaries, etc.) have a natural home and turning AI off cleanly
 * disables ALL of them in one click.
 *
 * The toggle is owner-only; non-owners see the current state read-only
 * with a hint to ask their workspace owner.
 */
export function AiTab() {
  const current = useCurrentCompany();
  const qc = useQueryClient();
  const toast = useToast();
  const companyId = current?.id;
  const isOwner = current?.role === 'owner';

  const { data: company } = useQuery({
    queryKey: ['company', companyId],
    queryFn: () => api.getCompany(companyId!),
    enabled: !!companyId,
  });

  const [saving, setSaving] = useState(false);

  if (!companyId || !company) return null;

  const aiEnabled = company.aiEnabled;

  const setAiEnabled = async (next: boolean) => {
    setSaving(true);
    try {
      await api.updateCompany(companyId, { aiEnabled: next });
      await qc.invalidateQueries({ queryKey: ['company', companyId] });
      toast.push({
        variant: 'success',
        title: next ? 'AI features enabled' : 'AI features disabled',
        description: next
          ? 'New tickets will get auto-generated titles.'
          : 'New tickets will be created without auto-generated titles.',
      });
    } catch (e) {
      toast.push({ variant: 'danger', title: 'Save failed', description: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h1 className={styles.h1}>AI</h1>
      <p className={styles.sub}>
        AI-powered features for this workspace. On for a new workspace;
        the owner can switch them off here.
      </p>

      <section className={styles.section}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{
            width: 32, height: 32,
            borderRadius: 8,
            background: 'var(--bg-muted)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: aiEnabled ? 'var(--hl-blue)' : 'var(--text-tertiary)',
            flexShrink: 0,
          }}>
            <Sparkles size={16} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                Auto-generate ticket titles
              </h3>
              <Toggle
                checked={aiEnabled}
                disabled={!isOwner || saving}
                onChange={setAiEnabled}
              />
            </div>
            <p style={{ margin: '6px 0 0', color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.6 }}>
              When you save a description on a new ticket, the description and
              source URL are sent to <strong>Anthropic's Claude</strong>{' '}
              to generate a short, descriptive title. Saves you the chore of
              writing one when filing a bug from the extension.
            </p>
            {!isOwner && (
              <p style={{ margin: '6px 0 0', color: 'var(--text-tertiary)', fontSize: 12 }}>
                Only the workspace owner can change this setting.
              </p>
            )}
          </div>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.05 }}>
            What happens when this is on
          </h4>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.8 }}>
            <li>New captures from the extension are created with a placeholder title.</li>
            <li>You type a description on the ticket page; it autosaves as you type.</li>
            <li>~30 seconds later, the worker sends your description (and source URL) to Anthropic.</li>
            <li>The title field updates live with a short, plain-English summary.</li>
            <li>You can edit the title manually anytime, and that always wins over the AI's choice.</li>
            <li>Walkthrough narration is sent to <strong>OpenAI</strong> to be transcribed, and the transcript to Anthropic's Claude to write up each ticket.</li>
          </ul>
        </div>

        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 16 }}>
          <h4 style={{ margin: '0 0 8px', fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.05 }}>
            What happens when this is off
          </h4>
          <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 13, lineHeight: 1.8 }}>
            <li>New captures keep their placeholder title (<code style={{ background: 'var(--bg-muted)', padding: '1px 4px', borderRadius: 3 }}>Untitled capture</code>) until you rename them.</li>
            <li>Descriptions still autosave as you type; that's a separate feature.</li>
            <li><strong>No ticket data is sent to an AI provider.</strong> Descriptions, screenshots, console logs and source URLs stay in Toado.</li>
            {/* Now true of transcription too (TOADO-454): walkthrough narration used to
                be transcribed by OpenAI whatever this setting said. */}
            <li>Walkthrough narration is not sent for transcription, so walkthrough tickets have no transcript.</li>
          </ul>
        </div>

        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-start',
          padding: 12, marginTop: 16,
          background: 'var(--bg-subtle)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.6,
        }}>
          <ShieldCheck size={14} style={{ flexShrink: 0, marginTop: 2, color: 'var(--hl-green, var(--text-tertiary))' }} />
          <div>
            <strong style={{ display: 'block', color: 'var(--text)', marginBottom: 2 }}>
              Privacy
            </strong>
            For titles we send the ticket <em>description</em> and <em>source URL</em>;
            for walkthroughs, the <em>narration</em> to OpenAI, and its <em>transcript</em>, with
            the walkthrough's ticket titles and your ticket type names, to Anthropic. Never
            screenshots, console logs, network requests, or DevTools data.
            Each provider's data-handling policy applies to what's sent:{' '}
            <a href="https://www.anthropic.com/legal/privacy" target="_blank" rel="noreferrer" style={{ color: 'var(--hl-blue)' }}>
              Anthropic
            </a>,{' '}
            <a href="https://openai.com/policies/privacy-policy" target="_blank" rel="noreferrer" style={{ color: 'var(--hl-blue)' }}>
              OpenAI
            </a>.
          </div>
        </div>

      </section>
    </>
  );
}

interface ToggleProps {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}

/**
 * Minimal switch-style toggle. Could move to @vd/ui if a second
 * caller appears — until then, inline keeps the cross-package surface
 * smaller.
 */
function Toggle({ checked, disabled, onChange }: ToggleProps) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        position: 'relative',
        width: 40, height: 22,
        borderRadius: 999,
        background: checked ? 'var(--hl-blue)' : 'var(--bg-muted)',
        border: '1px solid var(--border)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'background 120ms',
        flexShrink: 0,
        padding: 0,
      }}
    >
      <span style={{
        position: 'absolute',
        top: 2, left: checked ? 20 : 2,
        width: 16, height: 16,
        borderRadius: 999,
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
        transition: 'left 120ms',
      }} />
    </button>
  );
}
